import React from 'react';
import { translations } from '../../translations';
import './Pricing.css';

const PricingCard = ({ title, prices, description }) => (
  <div className="pricing__card">
    <h3 className="pricing__card-title">{title}</h3>
    <div className="pricing__card-prices">
      {prices.map((item, index) => (
        <div key={index} className="pricing__price-row">
          <span className="pricing__car-type">{item.type}</span>
          <span className="pricing__price">{item.price}</span>
        </div>
      ))}
    </div>
    <p className="pricing__card-description">{description}</p>
  </div>
);

const Pricing = ({ language = 'en' }) => {
  const t = translations[language]?.pricing || translations.en.pricing;
  const ct = t.carTypes;
  const p = t.packages;

  const monthlyPackages = [
    {
      title: p.exteriorMonthly.title,
      prices: [
        { type: ct.compact, price: '$48' },
        { type: ct.sedan, price: '$64' },
        { type: ct.suv, price: '$78' },
      ],
      description: p.exteriorMonthly.description,
    },
    {
      title: p.basicMonthly.title,
      prices: [
        { type: ct.compact, price: '$88' },
        { type: ct.sedan, price: '$112' },
        { type: ct.suv, price: '$134' },
      ],
      description: p.basicMonthly.description,
    },
    {
      title: p.superMonthly.title,
      prices: [
        { type: ct.compact, price: '$135' },
        { type: ct.sedan, price: '$160' },
        { type: ct.suv, price: '$185' },
      ],
      description: p.superMonthly.description,
    },
  ];

  const oneTimePackages = [
    {
      title: p.basicOneTime.title,
      prices: [
        { type: ct.compact, price: '$27' },
        { type: ct.sedan, price: '$35' },
        { type: ct.suv, price: '$42' },
      ],
      description: p.basicOneTime.description,
    },
    {
      title: p.superOneTime.title,
      prices: [
        { type: ct.compact, price: '$45' },
        { type: ct.sedan, price: '$55' },
        { type: ct.suv, price: '$70' },
      ],
      description: p.superOneTime.description,
    },
    {
      title: p.deluxeOneTime.title,
      prices: [
        { type: ct.compact, price: '$200' },
        { type: ct.sedan, price: '$235' },
        { type: ct.suv, price: '$280' },
      ],
      description: p.deluxeOneTime.description,
    },
  ];

  const individualServices1 = [
    {
      title: p.exteriorWash.title,
      prices: [
        { type: ct.compact, price: '$15' },
        { type: ct.sedan, price: '$20' },
        { type: ct.suv, price: '$24' },
      ],
      description: p.exteriorWash.description,
    },
    {
      title: p.interiorWash.title,
      prices: [
        { type: ct.compact, price: '$15' },
        { type: ct.sedan, price: '$20' },
        { type: ct.suv, price: '$24' },
      ],
      description: p.interiorWash.description,
    },
    {
      title: p.interiorDetailing.title,
      prices: [
        { type: ct.compact, price: '$30' },
        { type: ct.sedan, price: '$35' },
        { type: ct.suv, price: '$46' },
      ],
      description: p.interiorDetailing.description,
    },
  ];

  const individualServices2 = [
    {
      title: p.upholstery.title,
      prices: [
        { type: ct.compact, price: '$40' },
        { type: ct.sedan, price: '$80' },
        { type: ct.suv, price: '$100' },
      ],
      description: p.upholstery.description,
    },
    {
      title: p.polishing.title,
      prices: [
        { type: ct.compact, price: t.contactUs },
        { type: ct.sedan, price: t.contactUs },
        { type: ct.suv, price: t.contactUs },
      ],
      description: p.polishing.description,
    },
    {
      title: p.ceramic.title,
      prices: [
        { type: ct.compact, price: t.contactUs },
        { type: ct.sedan, price: t.contactUs },
        { type: ct.suv, price: t.contactUs },
      ],
      description: p.ceramic.description,
    },
  ];

  const individualServices3 = [
    {
      title: p.decontamination.title,
      prices: [
        { type: ct.compact, price: t.contactUs },
        { type: ct.sedan, price: t.contactUs },
        { type: ct.suv, price: t.contactUs },
      ],
      description: p.decontamination.description,
    },
    {
      title: p.headlight.title,
      prices: [
        { type: ct.compact, price: t.contactUs },
        { type: ct.sedan, price: t.contactUs },
        { type: ct.suv, price: t.contactUs },
      ],
      description: p.headlight.description,
    },
    {
      title: p.windshieldCeramic.title,
      prices: [
        { type: ct.compact, price: '$60' },
        { type: ct.sedan, price: '$60' },
        { type: ct.suv, price: '$60' },
      ],
      description: p.windshieldCeramic.description,
    },
  ];

  return (
    <section className="pricing" id="p">
      <div className="pricing__container">
        {/* Monthly Packages */}
        <div className="pricing__section pricing__section--gray">
          <h2 className="pricing__heading">{t.monthlyHeading}</h2>
          <div className="pricing__divider"></div>
          <p className="pricing__subtitle">{t.monthlySubtitle}</p>
          <div className="pricing__grid">
            {monthlyPackages.map((pkg, index) => (
              <PricingCard key={index} {...pkg} />
            ))}
          </div>
        </div>

        {/* One-Time Packages */}
        <div className="pricing__section pricing__section--white">
          <h2 className="pricing__heading">{t.oneTimeHeading}</h2>
          <div className="pricing__divider"></div>
          <p className="pricing__subtitle">{t.oneTimeSubtitle}</p>
          <div className="pricing__grid">
            {oneTimePackages.map((pkg, index) => (
              <PricingCard key={index} {...pkg} />
            ))}
          </div>
        </div>

        {/* Individual Services - Row 1 */}
        <div className="pricing__section pricing__section--gray">
          <h2 className="pricing__heading">{t.individualHeading}</h2>
          <div className="pricing__divider"></div>
          <p className="pricing__subtitle">{t.individualSubtitle}</p>
          <div className="pricing__grid">
            {individualServices1.map((pkg, index) => (
              <PricingCard key={index} {...pkg} />
            ))}
          </div>
        </div>

        {/* Individual Services - Row 2 */}
        <div className="pricing__section pricing__section--gray pricing__section--no-header">
          <div className="pricing__grid">
            {individualServices2.map((pkg, index) => (
              <PricingCard key={index} {...pkg} />
            ))}
          </div>
        </div>

        {/* Individual Services - Row 3 */}
        <div className="pricing__section pricing__section--gray pricing__section--no-header">
          <div className="pricing__grid">
            {individualServices3.map((pkg, index) => (
              <PricingCard key={index} {...pkg} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
